import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Calendar, 
  DollarSign, 
  Building2, 
  Clock, 
  TrendingUp,
  Briefcase,
  Users,
} from "lucide-react";

interface Contract {
  id: string;
  title: string;
  client: string;
  value: number;
  status: "Active" | "Pending" | "Expired";
  startDate: string;
  endDate: string;
  type: string;
  renewalDate: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Props {
  contract: Contract | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "Active":
      return "bg-green-100 text-green-800 border-green-200";
    case "Pending":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "Expired":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "Active":
      return "🟢";
    case "Pending":
      return "🟡";
    case "Expired":
      return "🔴";
    default:
      return "⚫";
  }
};

export function ViewContractDialog({ contract, open, onOpenChange }: Props) {
  if (!contract) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const DetailRow = ({ label, value, icon: Icon }: { label: string; value: string | number | React.ReactNode; icon?: React.ComponentType<{ className?: string }> }) => (
    <div className="flex items-center gap-3 py-2">
      {Icon && <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-600">{label}:</span>
        <span className="ml-2 text-sm text-gray-900">{value}</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-6 pb-0 pt-6">
          <DialogTitle className="sr-only">
            Contract Details - {contract.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View complete contract information including terms, dates, and financial details.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Contract Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900">{contract.title}</h1>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={getStatusColor(contract.status)}>
                      <span className="mr-1">{getStatusIcon(contract.status)}</span>
                      {contract.status}
                    </Badge>
                    <span className="text-sm text-gray-600">{contract.type}</span>
                  </div>
                </div>
          </div>
            </div>

            {/* Contract Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Contract Information
              </h2>
              <div className="space-y-3">
                <DetailRow 
                  label="Client" 
                  value={contract.client} 
                  icon={Users}
                />
                <DetailRow 
                  label="Contract Type" 
                  value={contract.type} 
                  icon={Briefcase}
                />
                <DetailRow 
                  label="Status" 
                  value={
                    <Badge className={getStatusColor(contract.status)}>
                      <span className="mr-1">{getStatusIcon(contract.status)}</span>
                      {contract.status}
                    </Badge>
                  }
                />
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                Financial Information
              </h2>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600">Contract Value</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(contract.value)}</p>
                  </div>
            </div>
          </div>
            </div>

            {/* Timeline Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Timeline Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <DetailRow 
                    label="Start Date" 
                    value={formatDate(contract.startDate)} 
                    icon={Calendar}
                  />
                  <DetailRow 
                    label="End Date" 
                    value={formatDate(contract.endDate)} 
                    icon={Calendar}
                  />
                </div>
                <div className="space-y-3">
                  <DetailRow 
                    label="Renewal Date" 
                    value={formatDate(contract.renewalDate)} 
                    icon={Clock}
                  />
                </div>
              </div>
            </div>

            {/* Contract Status Overview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Contract Status Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`rounded-lg p-4 border ${
                  contract.status === 'Active' 
                    ? 'bg-green-50 border-green-100' 
                    : contract.status === 'Pending'
                    ? 'bg-yellow-50 border-yellow-100'
                    : 'bg-red-50 border-red-100'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      contract.status === 'Active' 
                        ? 'bg-green-100' 
                        : contract.status === 'Pending'
                        ? 'bg-yellow-100'
                        : 'bg-red-100'
                    }`}>
                      <FileText className={`h-5 w-5 ${
                        contract.status === 'Active' 
                          ? 'text-green-600' 
                          : contract.status === 'Pending'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${
                        contract.status === 'Active' 
                          ? 'text-green-600' 
                          : contract.status === 'Pending'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}>
                        Current Status
                      </p>
                      <p className={`text-xl font-bold ${
                        contract.status === 'Active' 
                          ? 'text-green-700' 
                          : contract.status === 'Pending'
                          ? 'text-yellow-700'
                          : 'text-red-700'
                      }`}>
                        {contract.status}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-600">Contract Type</p>
                      <p className="text-xl font-bold text-blue-700">{contract.type}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


